import axios from "axios";

// ── Production Flows ─────────────────────────────────────────
// GET /v1/production/flow
export const getAllFlows = async (params = {}) => {
  const response = await axios.get("production/flow", { params });
  return response.data;
};

// GET /v1/production/flow/default
export const getDefaultFlow = async () => {
  const response = await axios.get("production/flow/default");
  return response.data;
};

// GET /v1/production/flow/:id
export const getFlowById = async (id) => {
  const response = await axios.get(`production/flow/${id}`);
  return response.data;
};

// POST /v1/production/flow
export const createFlow = async (data) => {
  const response = await axios.post("production/flow", data);
  return response.data;
};

// PUT /v1/production/flow/:id
export const updateFlow = async (id, data) => {
  const response = await axios.put(`production/flow/${id}`, data);
  return response.data;
};

// DELETE /v1/production/flow/:id
export const deleteFlow = async (id) => {
  const response = await axios.delete(`production/flow/${id}`);
  return response.data;
};

// POST /v1/production/flow/:id/set-default
export const setDefaultFlow = async (id) => {
  const response = await axios.post(`production/flow/${id}/set-default`);
  return response.data;
};

// ── Production Stages ────────────────────────────────────────
// GET /v1/production/stage
export const getAllStages = async (params = {}) => {
  const response = await axios.get("production/stage", { params });
  return response.data;
};

// GET /v1/production/stage/:id
export const getStageById = async (id) => {
  const response = await axios.get(`production/stage/${id}`);
  return response.data;
};

// POST /v1/production/stage/flow/:flowId
export const addStageToFlow = async (flowId, data) => {
  const response = await axios.post(`production/stage/flow/${flowId}`, data);
  return response.data;
};

// PUT /v1/production/stage/:id
export const updateStage = async (id, data) => {
  const response = await axios.put(`production/stage/${id}`, data);
  return response.data;
};

// DELETE /v1/production/stage/:id
export const deleteStage = async (id) => {
  const response = await axios.delete(`production/stage/${id}`);
  return response.data;
};

// POST /v1/production/stage/flow/:flowId/reorder
export const reorderStages = async (flowId, stageOrder) => {
  const response = await axios.post(
    `production/stage/flow/${flowId}/reorder`,
    { stage_order: stageOrder }
  );
  return response.data;
};

// ── Production Scheduling ────────────────────────────────────
// GET /v1/production/schedule/jobs/:stageCode
export const getSchedulableJobs = async (stageCode, params = {}) => {
  const response = await axios.get(
    `production/schedule/jobs/${stageCode}`,
    { params }
  );
  return response.data;
};

// GET /v1/production/schedule/batch-suggestions/:stageCode
export const getBatchSuggestions = async (stageCode) => {
  const response = await axios.get(
    `production/schedule/batch-suggestions/${stageCode}`
  );
  return response.data;
};

// POST /v1/production/schedule/batch
export const createBatch = async (data) => {
  const response = await axios.post("production/schedule/batch", data);
  return response.data;
};

// GET /v1/production/schedule/batch
export const getBatches = async (params = {}) => {
  const response = await axios.get("production/schedule/batch", { params });
  return response.data;
};

// GET /v1/production/schedule/batch/:id
export const getBatchById = async (id) => {
  const response = await axios.get(`production/schedule/batch/${id}`);
  return response.data;
};

// POST /v1/production/schedule/job
export const scheduleJob = async (data) => {
  const response = await axios.post("production/schedule/job", data);
  return response.data;
};

// POST /v1/production/schedule/batch/:batchId
export const scheduleBatch = async (batchId, data) => {
  const response = await axios.post(
    `production/schedule/batch/${batchId}`,
    data
  );
  return response.data;
};

// GET /v1/production/schedule/gantt
export const getGanttData = async (params) => {
  const response = await axios.get("production/schedule/gantt", { params });
  return response.data;
};

// GET /v1/production/schedule/capacity
export const getCapacityUtilization = async (params) => {
  const response = await axios.get("production/schedule/capacity", { params });
  return response.data;
};

// PUT /v1/production/schedule/:id/reschedule
export const rescheduleJob = async (id, data) => {
  const response = await axios.put(
    `production/schedule/${id}/reschedule`,
    data
  );
  return response.data;
};

// PUT /v1/production/schedule/:id/cancel
export const cancelSchedule = async (id) => {
  const response = await axios.put(`production/schedule/${id}/cancel`);
  return response.data;
};

// ── Production Instructions ──────────────────────────────────
// POST /v1/production/instruction/generate
export const generateInstruction = async (data) => {
  const response = await axios.post(
    "production/instruction/generate",
    data
  );
  return response.data;
};

// GET /v1/production/instruction/:id
export const getInstructionById = async (id) => {
  const response = await axios.get(`production/instruction/${id}`);
  return response.data;
};

// GET /v1/production/instruction/job/:jobId
export const getInstructionsForJob = async (jobId) => {
  const response = await axios.get(`production/instruction/job/${jobId}`);
  return response.data;
};

// GET /v1/production/instruction/:id/print
export const printInstruction = async (id) => {
  const response = await axios.get(`production/instruction/${id}/print`);
  return response.data;
};

// POST /v1/production/instruction/:id/complete
export const completeInstruction = async (id, data) => {
  const response = await axios.post(
    `production/instruction/${id}/complete`,
    data
  );
  return response.data;
};

// ── Kanban Helper ────────────────────────────────────────────
// Fetches the default flow stages then loads jobs for each stage
// Returns { stages: [...], columns: { [stageCode]: { stage, jobs } } }
export const getKanbanBoard = async (filters = {}) => {
  const flow = await getDefaultFlow();
  const stages = flow.stages || [];

  const params = {};
  if (filters.priority) params.priority = filters.priority;
  if (filters.fulfillment_status)
    params.fulfillment_status = filters.fulfillment_status;

  // Fetch jobs for every stage in parallel
  const jobResults = await Promise.allSettled(
    stages.map((stage) => getSchedulableJobs(stage.code, params))
  );

  const columns = {};
  stages.forEach((stage, idx) => {
    const result = jobResults[idx];
    columns[stage.code] = {
      stage,
      jobs:
        result.status === "fulfilled" ? result.value.jobs || [] : [],
      total:
        result.status === "fulfilled" ? result.value.total || 0 : 0,
    };
  });

  return { flow, stages, columns };
};

export default {
  // Flows
  getAllFlows,
  getDefaultFlow,
  getFlowById,
  createFlow,
  updateFlow,
  deleteFlow,
  setDefaultFlow,
  // Stages
  addStageToFlow,
  updateStage,
  deleteStage,
  reorderStages,
  // Scheduling
  getSchedulableJobs,
  getBatchSuggestions,
  createBatch,
  getBatches,
  getBatchById,
  scheduleJob,
  scheduleBatch,
  getGanttData,
  getCapacityUtilization,
  rescheduleJob,
  cancelSchedule,
  // Instructions
  generateInstruction,
  getInstructionById,
  getInstructionsForJob,
  printInstruction,
  completeInstruction,
  // Kanban helper
  getKanbanBoard,
};
